import { useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { AppBar, Box, ClickAwayListener, Paper, Popper, Toolbar } from '@mui/material';

// project import
import Search from './Search';
import Profile from './Profile';
import Localization from './Localization';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

// assets
import { MoreOutlined } from '@ant-design/icons';

// hooks
import useAuth from 'hooks/useAuth';
import useDynamicConfig from 'hooks/useDynamicConfig';
import useEnums from 'hooks/useEnum';
import useSkipCompanyQuery from 'hooks/useSkipCompanyQuery';

// store
import { useGetCompanyQuery } from 'store/reducers/api/company';

// ==============================|| HEADER CONTENT - MOBILE ||============================== //

const MobileSection = () => {
  const theme = useTheme();
  const { activeRole } = useAuth();
  const { UserRolesEnum } = useEnums();
  const { SYSTEM_ROLES, COMPANY_ROLES, PROPERTY_ROLES } = useDynamicConfig();
  const skipCompanyQuery = useSkipCompanyQuery(activeRole);
  const { data: userCompany } = useGetCompanyQuery(activeRole?.company_id!, { skip: skipCompanyQuery });

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<any>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const iconBackColorOpen = theme.palette.mode === 'dark' ? 'grey.200' : 'grey.300';
  const iconBackColor = theme.palette.mode === 'dark' ? 'background.default' : 'grey.100';

  const hasPermissionForSearch = useMemo(() => {
    const { user_role_type_id } = activeRole || {};
    if (!user_role_type_id) return false;
    if (user_role_type_id === UserRolesEnum.COMPANY_INSTALLER) return false;

    if (userCompany) {
      return userCompany.company_type_id !== 3;
    }

    return [...SYSTEM_ROLES, ...COMPANY_ROLES, ...PROPERTY_ROLES].includes(user_role_type_id);
  }, [activeRole, SYSTEM_ROLES, COMPANY_ROLES, userCompany, UserRolesEnum, PROPERTY_ROLES]);

  return (
    <>
      <Box sx={{ flexShrink: 0, ml: 2 }}>
        <IconButton
          sx={{ color: 'text.primary', bgcolor: open ? iconBackColorOpen : iconBackColor }}
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          color="secondary"
          variant="light"
        >
          <MoreOutlined />
        </IconButton>
      </Box>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        style={{
          width: '100%'
        }}
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            <Paper sx={{ boxShadow: theme.customShadows.z1 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <AppBar color="inherit">
                  <Toolbar>
                    {hasPermissionForSearch ? <Search /> : <Box sx={{ width: '100%', ml: { xs: 0, md: 1 } }} />}
                    <Localization />
                    <Profile />
                  </Toolbar>
                </AppBar>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default MobileSection;
